import {Injectable} from '@angular/core';
import {BehaviorSubject, forkJoin} from 'rxjs';
import {environment} from '@environments/environment';
import {HttpClient} from '@angular/common/http';
import {ToastrService} from 'ngx-toastr';
import {map, take} from 'rxjs/operators';
import {IDashboardDataDownloadInfo, Segment} from '@app/components/projects/surveys/results/dashboard/dashboard.service';

interface IUxInteraction {
  title: string;
  display: boolean;
  refresh: boolean;
  downloadInfo: IDashboardDataDownloadInfo;
}

const defaultUxInteraction: IUxInteraction = {
  title: 'string',
  display: false,
  refresh: false,
  downloadInfo: {
    isDownloading: false,
    progress: {
      value: 0,
      max: 0,
    },
    segments: [],
    totalSizeInMo: 0,
  }
}

@Injectable({
  providedIn: 'root'
})
export class EmailStatusDetailsService {

  private BASE_URL = environment.apiUrl;

  public status$: BehaviorSubject<IUxInteraction> = new BehaviorSubject<IUxInteraction>(defaultUxInteraction);
  private segments: Segment[] = [];

  public data$: BehaviorSubject<[]> = new BehaviorSubject<[]>([]);
  public isSendingAgain$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
  ) {
  }

  clearData() {
    this.status$.next(defaultUxInteraction);
    this.data$.next([]);
  }

  displayDetails() {
    this.status$.next({
      ...this.status$.value,
      display: true,
    });
  }

  hideDetails() {
    this.status$.next({
      ...this.status$.value,
      display: false,
    });
    this.clearData();
  }

  displayNotDelivered(campaignUuid, waveUuid) {
    this.waveStatusDetailsWithQuery(
      'Not delivered',
      campaignUuid,
      waveUuid,
      {'status.delivered': {$ne: true}}
    );
  }
  displayFailed(campaignUuid, waveUuid) {

    this.waveStatusDetailsWithQuery(
      'Failed',
      campaignUuid,
      waveUuid,
      {'status.failed': {$eq: true}}
    );

  }

  private waveStatusDetailsWithQuery(title, campaignUuid, waveUuid, query) {

    const currentStatus = this.status$.value;
    if (currentStatus.downloadInfo.isDownloading || currentStatus.refresh) {
      this.toastr.warning(`Please wait for the current process to finish.`);
      return;
    }

    this.status$.next({
      ...this.status$.value,
      title,
      refresh: true,
    });

    this.displayDetails();

    console.log('[1] Calling waveStatusDetailsWithQuery', query);

    const url = `${this.BASE_URL}/publications/emailCampaignActions`;
    return this.http.post<any>(url, {
      action: 'waveStatusDetailsWithQuery',
      params: {
        campaignUuid,
        waveUuid,
        query,
      }
    }).subscribe({
      next: response => {

        console.log('[2] Received response from waveStatusDetailsWithQuery', response.data.datas);
        const {contentLength = 0, jsonUrl = null} = response.data.datas;

        //
        //
        //
        const segmentMoSize = 5;
        const totalSize = contentLength;
        const segmentSize = segmentMoSize * 1024 * 1024; // 5 Mo en octets

        console.log(`[load details:] - Téléchargement partiel (par paquet de ${segmentMoSize}Mo/${contentLength / 1024 / 1024})`);

        if (contentLength === 0) {
          this.toastr.warning(`No data to download.`, title);
          this.resetLoadingState();
          return;
        }

        this.downloadSegments(jsonUrl, segmentSize, totalSize)
          .pipe(take(1))
          .subscribe((data) => {
            this.data$.next(data);
            this.status$.next({
              ...this.status$.value,
              display: true,
              refresh: false,
              downloadInfo: {
                ...this.status$.value.downloadInfo,
                isDownloading: false,
              }
            });
          });

        //
        //
        //
        this.status$.next({
          ...this.status$.value,
          refresh: false,
        });
      },
      error: err => {
        console.log(`[Error]
      waveStatusDetailsWithQuery ->`, err);
      },
    });


  }

  downloadSegments(url: string, segmentSize: number, totalSize: number) {
    const segments = [];

    this.status$.next({
      ...this.status$.value,
      refresh: false,
    });

    for (let start = 0, index = 0; start < totalSize; start += segmentSize, index++) {

      this.status$.next({
        ...this.status$.value,
        downloadInfo: {
          ...this.status$.value.downloadInfo,
          isDownloading: true,
          totalSizeInMo: Math.floor(totalSize / 1024 / 1024),
          progress: {
            value: 0,
            max: index
          },
        }
      });

      this.segments[index] = {
        isDownloaded: false,
        data: '',
      };

      const end = Math.min(start + segmentSize - 1, totalSize - 1);

      // console.log(end);

      const headers = {
        headers: {
          Range: `bytes=${start}-${end}`
        }
      };

      segments.push(this.http.get(url, {
          ...headers,
          responseType: 'text'
        }).pipe(map(data => {

          // Met à jour le tableau de segments
          const segment: Segment = {
            data,
            isDownloaded: true
          };
          this.segments[index] = segment;

          this.status$.next({
            ...this.status$.value,
            downloadInfo: {
              ...this.status$.value.downloadInfo,
              isDownloading: true,
              progress: {
                ...this.status$.value.downloadInfo.progress,
                value: this.status$.value.downloadInfo.progress.value + 1,
              },
              segments: [
                ...this.status$.value.downloadInfo.segments,
                this.segments[index], // ?....
              ]
            }
          });

          return data;
        }))
      );

    }

    return forkJoin(segments)
      .pipe(
        map(segmentArray => {
          const jsonString = segmentArray.join('');
          const jsonData = JSON.parse(jsonString);

          this.status$.next({
            ...this.status$.value,
            downloadInfo: {
              ...this.status$.value.downloadInfo,
              progress: {
                ...this.status$.value.downloadInfo.progress,
                value: this.status$.value.downloadInfo.progress.max,
              },
            }
          });

          setTimeout(() => {
            this.status$.next({
              ...this.status$.value,
              downloadInfo: {
                ...this.status$.value.downloadInfo,
                isDownloading: false,
              }
            });
          }, 500);

          return jsonData;
        })
      );
  }


  private resetLoadingState() {
    this.status$.next({
      ...this.status$.value,
      refresh: false,
      downloadInfo: {
        ...this.status$.value.downloadInfo,
        isDownloading: false,
      }
    });
  }

  public reSendWaveForSpecificEmails(campaignUuid, waveUuid, emails: string[]) {

    this.isSendingAgain$.next(true);

    this.toastr.info(`Starting to send ${emails.length} messages...`);

    const url = `${this.BASE_URL}/publications/emailCampaignActions`;
    return this.http.post<any>(url, {
      // action: 'migrateEmailLogs',
      action: 'reSendWaveForSpecificEmails',
      params: {
        campaignUuid,
        waveUuid,
        emails,
      }
    }).subscribe({
      next: response => {
        this.toastr.success(`${emails.length} messages has been resent!`);
        this.isSendingAgain$.next(false);
      },
      error: err => {
        this.toastr.error(err);
        console.log(`[Error] testElasticCnx ->`, err);
      },
    });

  }


}
